import React from "react";
import { createRoot } from "react-dom/client";
import "./instrument";

import dayjs from "dayjs";
import "./i18n";
import { isParentHost } from "./portal/base";

import WeekDay from "dayjs/plugin/weekday";
import LocaleData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";

// The admin tree and the parent portal are both lazy so only ONE of them is
// ever evaluated per page load. Module evaluation (not rendering) is what
// instantiates each tree's Supabase client, so a static import of either
// would create both clients on every page: "Multiple GoTrueClient instances"
// in the console and, worse, the admin client consuming a parent magic-link
// hash into the admin session slot.
const AdminRoot = React.lazy(() => import("./AdminRoot"));
const PortalApp = React.lazy(() => import("./portal/PortalApp"));

dayjs.extend(WeekDay);
dayjs.extend(LocaleData);
dayjs.extend(LocalizedFormat);

const container = document.getElementById("root");
// eslint-disable-next-line
const root = createRoot(container!);
// No React.StrictMode: it is a no-op in production, but in development it
// double-runs @react-google-maps/api's init effect, which boots TWO
// google.maps.Map instances onto one div. Which instance ends up owning the
// tiles is racy — maps intermittently rendered grey and ignored fitBounds —
// and this codebase had already grown per-component workarounds for the
// double-mount (OrganizationProvider, EnrollmentMap). Dropping the wrapper
// makes development behave like production.
//
// The parent portal is a sibling app, not an admin page: it has its own
// session (separate Supabase storage key), no tenant resolution, and no
// refine. Branching here keeps it outside OrganizationProvider, whose boot
// screen blocks on a profiles/organizations lookup that parents don't have.
// A dedicated parent hostname gets the portal for every path (see
// portal/base.ts); the admin hostnames only under /portal.
const isPortalRoute =
  isParentHost ||
  window.location.pathname === "/portal" ||
  window.location.pathname.startsWith("/portal/");

root.render(
  <React.Suspense fallback={null}>
    {isPortalRoute ? <PortalApp /> : <AdminRoot />}
  </React.Suspense>,
);
